import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Question, QuestionResponse, Section } from '../../../interfaces/questions.interface';
import { CstQuestionnaireService, EntityType } from '../../../services/cst-questionnaire.service';
import { AuthService } from '../../../../auth/services/auth.service';
import * as uuid from 'uuid';
import { SingleChoiceQuestion } from '../../../interfaces/single-selection-question.interface';
import { MultipleSelectionQuestion } from '../../../interfaces/multiple-selection.question';
import { FormQuestion } from '../../../interfaces/form-question.interface';
import { FreeTextQuestion } from '../../../interfaces/free-text-question.interface';
import { MatrixQuestion } from '../../../interfaces/matrix-question.interface';
import * as _ from 'lodash';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs/internal/Subscription';
import { CommonQuestionnaireService } from '../../../services/common-questionnaire.service';
import { QuestionTypesEnum } from '../../../enums/question-types.enum';
import { CifcReview } from '../../../interfaces/cifc.interface';
import { CustomerQuestionnaireService } from '../../../services/customer-questionnaire.service';
import { Observable, Subject } from 'rxjs';

export interface OptionInterface {
    displayTitle: string;
    value: QuestionTypesEnum | 'section';
}

@Component({
    selector: 'app-overview-sidenav',
    templateUrl: './overview-sidenav.component.html',
    styleUrls: ['./overview-sidenav.component.scss'],
    standalone: false,
})
export class OverviewSidenavComponent implements OnInit, OnDestroy {
    @Output() createOptionClicked = new EventEmitter();
    @Output() allQuestionsAreReviewed = new EventEmitter();
    @Output() scrollToQuestionEmitter = new EventEmitter();

    public fullVersion = true;
    public sections: Section[] = [];
    /*** @var createOptions - used for displaying the options in the create options panel */
    public createOptions: OptionInterface[] = [
        { displayTitle: 'Section', value: 'section' },
        { displayTitle: 'Text', value: QuestionTypesEnum.FREE },
        { displayTitle: 'Form', value: QuestionTypesEnum.FORM },
        { displayTitle: 'Single choice', value: QuestionTypesEnum.SINGLE },
        { displayTitle: 'Multiple choice', value: QuestionTypesEnum.MULTIPLE },
        { displayTitle: 'Matrix', value: QuestionTypesEnum.MATRIX },
    ];
    public panelOpenState = false;
    public sectionsHasAtLeastOneReview: boolean[];
    public sectionsHasAtLeastOneAnsweredReview: boolean[];

    public draggedEntity: QuestionResponse;
    public fromSectionIndex: number;
    public fromQuestionIndex: number;

    public subscription: Subscription;
    public questionnaireLoadedSubscription: Subscription;
    public displayVersionSubscription: Subscription;

    public reviewedQuestions: Question[] = [];
    public filteredSectionsSubject: Subject<Section[]> = new Subject<Section[]>();

    constructor(
        public customerQuestionnaireService: CustomerQuestionnaireService,
        public cstQuestionnaireService: CstQuestionnaireService,
        public commonQuestionnaireService: CommonQuestionnaireService,
        public authService: AuthService,
        public cdr: ChangeDetectorRef,
    ) {
        this.subscription = this.customerQuestionnaireService.onQuestionMarked().subscribe((question: Question) => {
            if (this.commonQuestionnaireService.reviewMode) {
                if (this.reviewedQuestions.filter((x) => x.id === question.id).length === 0) {
                    this.reviewedQuestions.push(question);
                }
                const areAllQuestionReviewed =
                    this.reviewedQuestions.length === this.customerQuestionnaireService.getAnsweredQuestionsForReview().length;
                if (areAllQuestionReviewed) {
                    this.allQuestionsAreReviewed.emit(true);
                }
            }
            setTimeout(() => {
                this.checkSectionsHaveAtLeastOneReview();
                this.checkSectionsHaveAtLeastOneAnsweredReview();

                this.cdr.detectChanges();
            }, 200);
        });

        if (!this.commonQuestionnaireService.createMode && !this.commonQuestionnaireService.reviewMode) {
            if (this.customerQuestionnaireService.displayVersion === 'condensed') {
                this.fullVersion = !this.fullVersion;
                this.customerQuestionnaireService.setDisplayVersion('condensed');
            }
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.questionnaireLoadedSubscription?.unsubscribe();
        this.displayVersionSubscription?.unsubscribe();
    }

    public ngOnInit() {
        this.getFilteredSectionsObs().subscribe((sections: Section[]) => {
            this.sections = sections;
        });

        this.displayVersionSubscription = this.customerQuestionnaireService.getDisplayVersionObs().subscribe((version: string) => {
            this.fullVersion = version === 'full';
        });

        if (this.commonQuestionnaireService.createMode) {
            this.questionnaireLoadedSubscription = this.cstQuestionnaireService.isQuestionnaireLoaded().subscribe(() => {
                this.sections = this.cstQuestionnaireService.sections;
                this.cdr.detectChanges();
            });
        } else {
            this.questionnaireLoadedSubscription = this.customerQuestionnaireService
                .isQuestionnaireLoaded()
                .subscribe((customerQuestionnaireLoaded: boolean) => {
                    if (customerQuestionnaireLoaded) {
                        this.sections = this.customerQuestionnaireService.getSections();
                        if (this.sections.length) {
                            /*** highlight first question */
                            this.commonQuestionnaireService.onQuestionSelected(
                                this.sections[0].questions[0] as unknown as QuestionResponse,
                                this.sections[0],
                            );

                            this.sectionsHasAtLeastOneReview = new Array(this.sections.length).fill(false);

                            if (this.commonQuestionnaireService.reviewMode) {
                                this.sectionsHasAtLeastOneAnsweredReview = new Array(this.sections.length).fill(false);
                                this.checkSectionsHaveAtLeastOneAnsweredReview();
                            }
                            this.checkSectionsHaveAtLeastOneReview();
                        }
                    }
                });
        }
    }

    public drop(event: CdkDragDrop<string[]>, toSectionIndex: number) {
        if (!this.commonQuestionnaireService.createMode) {
            return;
        }

        let newQuestion = _.cloneDeep(this.draggedEntity);
        newQuestion.id = uuid.v4();
        this.cstQuestionnaireService.currentSectionSubject.next(this.sections[toSectionIndex]);

        this.cstQuestionnaireService.moveQuestion(
            this.draggedEntity as unknown as Question,
            this.fromQuestionIndex,
            this.fromSectionIndex,
            event.currentIndex,
            toSectionIndex,
        );
        this.cstQuestionnaireService.isDragDropActive = true;
    }

    public setDraggedItem(question: QuestionResponse, sectionIndex: number, questionIndex: number) {
        this.draggedEntity = question;
        this.fromSectionIndex = sectionIndex;
        this.fromQuestionIndex = questionIndex;
    }

    /** Function used to map the question type to the proper icon when it's being added to the sidepanel */
    public mapQuestionToIcon(type: QuestionTypesEnum) {
        switch (type) {
            case QuestionTypesEnum.FREE:
                return 'ds-icon-text_question';
            case QuestionTypesEnum.FORM:
                return 'ds-icon-form_question';
            case QuestionTypesEnum.SINGLE:
                return 'ds-icon-single_choice_question';
            case QuestionTypesEnum.MULTIPLE:
                return 'ds-icon-multiple_choice_question';
            case QuestionTypesEnum.MATRIX:
                return 'ds-icon-matrix_question';
            default:
                return '';
        }
    }

    /** Function used to auto-scroll to specific question selected by CIFC for review */
    public scrollToQuestion(question: QuestionResponse) {
        this.scrollToQuestionEmitter.emit(question);
    }

    public selectQuestionFromSidenav(question: QuestionResponse, section: Section) {
        this.commonQuestionnaireService.onQuestionSelected(question, section, false);
        this.scrollToQuestion(question);
    }

    /** Function used to focus the panel in order to close it on focus out */
    public onOpenedPanel($event: any) {
        setTimeout(() => {
            (document.getElementById('cq-create-options') as HTMLElement).focus();
        }, 0);
    }

    /*** Function triggered when the PLUS button is being clicked
     * First it notifies the service of the type of option chosen by the user
     * Then it checks if the user wants to create a section or a type of question
     * If he wants a section then we use the addSection function
     * If hw wants a type of question we use the addQuestion function
     * In the end we refresh the sections with the newly updated version
     */
    public onCreateOption(option: EntityType) {
        if (!this.cstQuestionnaireService.currentQuestionForm || this.cstQuestionnaireService.currentQuestionForm.group.valid) {
            this.panelOpenState = false;
            this.cstQuestionnaireService.setCreatedType(option);

            /** Check if the user has selected to add a section */
            if (option.name === 'section') {
                this.cstQuestionnaireService.sections.forEach((section) => {
                    section.isExpanded = false;
                });
                const section: Section = {
                    id: uuid.v4(),
                    number: this.cstQuestionnaireService.sections.length + 1,
                    section: '',
                    questions: [],
                    isNew: true,
                    isExpanded: true,
                };
                this.cstQuestionnaireService.addSection({ entity: section });
            } else {
                const currentSection =
                    this.cstQuestionnaireService.currentSection ||
                    this.cstQuestionnaireService.sections[this.cstQuestionnaireService.sections.length - 1];
                this.cstQuestionnaireService.currentSection.isExpanded = true;
                const idQuestion = this.cstQuestionnaireService.getSectionById(currentSection.id)?.questions.length;
                const newQuestion: Question = {
                    answerChanged: false,
                    allow_attachment: true,
                    number: idQuestion || 0,
                    title: '',
                    service_offering: _.cloneDeep(this.cstQuestionnaireService.defaultQuestionTypes),
                    type_id: option.name,
                    required: false,
                    body: {} as SingleChoiceQuestion | MultipleSelectionQuestion | FormQuestion | FreeTextQuestion | MatrixQuestion,
                    cifc_review: {} as CifcReview,
                    section_id: currentSection.id,
                    conditional_logic: false,
                    has_other_option_checked: { checked: false, placeholder: '' },
                    isNew: true,
                };

                this.cstQuestionnaireService.addQuestion({ entity: newQuestion }, currentSection.id, option.name);

                this.commonQuestionnaireService.onQuestionSelected(newQuestion as unknown as QuestionResponse, currentSection);
            }
            this.sections = this.cstQuestionnaireService.sections;
            this.cstQuestionnaireService.currentQuestionForm?.group.markAllAsTouched();
            this.cstQuestionnaireService.showErrorOnForm.next(true);
        }
    }

    public changePanelOpenDisplay() {
        this.panelOpenState = !this.panelOpenState;
    }

    public selectQuestion(question: QuestionResponse, section: Section) {
        if (
            this.commonQuestionnaireService.createMode &&
            this.cstQuestionnaireService.currentQuestionForm &&
            !this.cstQuestionnaireService.currentQuestionForm.group.valid
        ) {
            this.cstQuestionnaireService.currentQuestionForm.group.markAllAsTouched();
        } else {
            this.commonQuestionnaireService.onQuestionSelected(question, section);
            setTimeout(() => {
                this.cdr.detectChanges();
            }, 100);
        }
    }

    /** Function used to change the background of the question icon depending on it's type */
    public getBackgroundForQuestionType(type: QuestionTypesEnum) {
        switch (type) {
            case QuestionTypesEnum.FREE:
                return '#94def2';
            case QuestionTypesEnum.SINGLE:
                return '#f294a7';
            case QuestionTypesEnum.MULTIPLE:
                return '#f0f06e';
            case QuestionTypesEnum.MATRIX:
                return '#96f294';
            case QuestionTypesEnum.FORM:
                return '#b36ef0';
            default:
                return '#f0c56e';
        }
    }

    /** Function used to toggle the full/condensed version */
    public switchVersion() {
        this.fullVersion = !this.fullVersion;
        this.customerQuestionnaireService.setDisplayVersion(this.fullVersion ? 'full' : 'condensed');
        this.customerQuestionnaireService.questionnaireResumed = true;
        localStorage.setItem('isQuestionnaireResumed', 'true');
    }

    /***
     *  Function used to filter the questions depending on the current version display type
     *  @param questions - questions to be filtered
     */
    public filterQuestions(questions: Question[]): QuestionResponse[] {
        if (this.commonQuestionnaireService.reviewMode) {
            questions = questions.filter((question: Question) => {
                let answeredQuestion = question as unknown as QuestionResponse;
                return answeredQuestion.body.answered && !answeredQuestion.body.deleted;
            });
        }

        if (!this.commonQuestionnaireService.reviewMode && !this.commonQuestionnaireService.createMode) {
            questions = this.commonQuestionnaireService.hideQuestionsBasedOnHashMap(questions);
        }

        return this.fullVersion
            ? (questions as unknown as QuestionResponse[])
            : (questions.filter((question: Question) => question.required) as unknown as QuestionResponse[]);
    }

    /***
     * Set current section to the opened panel from sidenav in order to add questions under the right section
     * @param section - where to add questions
     */
    public sectionOpened(section: Section) {
        if (
            this.commonQuestionnaireService.createMode &&
            ((this.cstQuestionnaireService.currentQuestionForm && this.cstQuestionnaireService.currentQuestionForm.group.valid) ||
                !this.cstQuestionnaireService.currentQuestionForm)
        ) {
            this.cstQuestionnaireService.currentSection = section;
            if (section.questions.length > 0) {
                this.commonQuestionnaireService.onQuestionSelected(section.questions[0] as unknown as QuestionResponse, section);
            }
        }
    }

    public filterSections(sections: Section[]): Section[] {
        let sectionsArr: Section[] = [];
        sections.forEach((section: Section) => {
            if (this.filterQuestions(section.questions).length) {
                sectionsArr.push(section);
            }
        });

        return this.commonQuestionnaireService.createMode ? sections : sectionsArr;
    }

    public getFilteredSectionsObs(): Observable<Section[]> {
        let sections: Section[] = this.sections;

        if (this.commonQuestionnaireService.createMode || this.commonQuestionnaireService.reviewMode) {
            this.filteredSectionsSubject.next(sections);
            return this.filteredSectionsSubject.asObservable();
        }

        return this.filteredSectionsSubject.asObservable();
    }

    /***
     * Check if we have at least one question with a review request/amend
     */
    public checkSectionsHaveAtLeastOneReview() {
        if (this.commonQuestionnaireService.createMode) {
            return;
        }

        this.sections.forEach((section: Section, index: number) => {
            if (!this.commonQuestionnaireService.reviewMode && !this.commonQuestionnaireService.createMode) {
                this.sectionsHasAtLeastOneReview[index] =
                    section.questions.filter(
                        (question: Question) => question.cifc_review && !question.cifc_review.answered && !question.cifc_review.solved,
                    ).length > 0;
            } else if (this.commonQuestionnaireService.reviewMode) {
                this.sectionsHasAtLeastOneReview[index] =
                    section.questions.filter(
                        (question: Question) => question.cifc_review && question.cifc_review.id && !question.cifc_review.solved,
                    ).length > 0;
            }
        });
    }

    /**
     * Check if we have at least one question with an answered review
     */
    public checkSectionsHaveAtLeastOneAnsweredReview() {
        if (this.commonQuestionnaireService.reviewMode) {
            this.sections.forEach((section: Section, index: number) => {
                this.sectionsHasAtLeastOneAnsweredReview[index] =
                    section.questions.filter((question: Question) =>
                        this.cstQuestionnaireService.isQuestionAnsweredAfterReview(question as unknown as QuestionResponse),
                    ).length > 0;
            });
        }
    }

    public selectSection(section: Section) {
        if (
            this.commonQuestionnaireService.createMode &&
            this.cstQuestionnaireService.currentQuestionForm &&
            this.cstQuestionnaireService.currentQuestionForm.group.valid
        ) {
            this.cstQuestionnaireService.currentSection = section;
            this.commonQuestionnaireService.markEntityAsActive({ entity: section });
        }
    }
}
